import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import to from 'await-to-js'
import { toast } from 'react-toastify'
import { useState, useEffect } from 'react'
import ChainHeader from 'components/Cards/ChainHeaderCard'
import NotFoundCard from 'components/NotFoundCard/NotFoundCard'
import { FBPage } from 'components/FbUI/Page/FBPage'
import FbButton from 'components/FbUI/FbButton'
import { Block } from 'components/UI/Block'
import confirm from 'components/dialogConfirm'
import NotesSection from 'components/Notes/NotesSection/NotesSection'
import useDocumentTitle from 'components/useDocumentTitle'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { getIdWithPrefix } from 'utils/pathUtils'
import { ContactCompanyType, IDoorProxy } from 'models/contact_companies'
import apiService from 'services/api'
import { CompanyAttributesManager } from 'components/CompanyAttrsManager/CompanyAttrsManager'
import DealsSection from 'components/Deals/DealsSection/DealsSection'
import ContactsTableSection from 'components/Cards/ContactsTableSection'
import { EditDoorProxyModal } from 'components/Modals/CompanyModal/EditDoorProxyModal'
import EmailsTableSection from 'components/Cards/EmailsTableSection'
import { featureFlagService } from 'utils/featureFlagService'
import { NewUnmatchedLocationPage } from './new-company-pages/NewUnmatchedLocationPage'
import LoadingScreen from 'components/Layout/LoadingScreen'

export function DoorProxyPage() {
  const { enableNewCompanyPage, isLoading } = featureFlagService()
  const urlParams = useParams()
  const { preferences } = usePreferences()

  const doorProxyID = urlParams?.door_proxy_id
    ? getIdWithPrefix(
        urlParams.door_proxy_id,
        preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]
      )
    : undefined

  if (enableNewCompanyPage) {
    return <NewUnmatchedLocationPage doorProxyID={doorProxyID!} />
  }

  return <_DoorProxyPage doorProxyID={doorProxyID!} isLoading={isLoading} />
}

function _DoorProxyPage({
  doorProxyID,
  isLoading,
}: {
  doorProxyID: number
  isLoading: boolean
}) {
  const navigate = useNavigate()
  const { preferences } = usePreferences()
  const [notFound, setNotFound] = useState(false)
  const [editDoorProxy, setEditDoorProxy] = useState<IDoorProxy | null>(null)

  const { enableEmailIntegration } = featureFlagService()

  const api = apiService()
  const {
    data: doorProxyData,
    isLoading: isLoadingDoorProxy,
    isError,
    error,
  } = api.useGetDoorProxy(doorProxyID!)

  // Get chain proxy data if needed for category and subcategory
  const chainProxyId = doorProxyData?.chain_proxy as number | undefined
  const { data: chainProxyData } = api.useGetCompany(chainProxyId ?? 0)

  useDocumentTitle(`${doorProxyData?.name || 'Not Found'} (Door) - First Bite`)

  useEffect(() => {
    if (
      !doorProxyID ||
      (isError && axios.isAxiosError(error) && error?.response?.status === 404)
    ) {
      setNotFound(true)
    }
  }, [error, isError, doorProxyID])

  // DOOR TYPE REDIRECT
  useEffect(() => {
    if (doorProxyData?.restaurant_id) {
      // redirect to restaurant page
      navigate(`/restaurant/${doorProxyData.restaurant_id}`)
    }
    if (doorProxyData?.lodging_id) {
      // redirect to lodging door page
      navigate(`/lodging/door/${doorProxyData.lodging_id}`)
    }
    if (doorProxyData?.cstore_id) {
      // redirect to convenience store door page
      navigate(`/convenience/door/${doorProxyData.cstore_id}`)
    }
  }, [doorProxyData, navigate])

  if (notFound) {
    return <NotFoundCard />
  }

  if (isLoadingDoorProxy || isLoading) {
    return <LoadingScreen />
  }

  const handleDelete = async () => {
    if (
      await confirm(
        'Deleting this door will also delete any notes, tags, deals, or contacts you may have added. This action cannot be undone.',
        'Delete Door',
        undefined,
        {
          buttonVariant: 'danger',
          confirmLabel: 'Delete Door',
        }
      )
    ) {
      if (!doorProxyData?.id) {
        return
      }
      const [err] = await to(api.deleteDoorProxy(doorProxyData.id))
      if (err) {
        toast.error('Error deleting door')
        return
      }
      toast.success('Door deleted')
      navigate('/')
    }
  }

  // Get the category and subcategory
  const category = chainProxyData?.category || 'Door'
  const subCategory = chainProxyData?.sub_category?.name || 'Door'
  const chainTableTypeSlug =
    chainProxyData?.sub_category?.slug || 'restaurants-bars'

  // For generic UGC doors, use the unmatched-doors-table type
  // This uses the generic contacts/doorproxies endpoint for all door operations
  const doorTableTypeSlug = 'unmatched-doors-table'

  // Prepare breadcrumbs with chain information if available
  const breadcrumbs = [
    chainProxyData && {
      title: 'Company',
      breadcrumb: chainProxyData.name || '',
      url: `/company/${preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]}${chainProxyData.id}`,
    },
    {
      title: 'Door',
      breadcrumb: doorProxyData?.name || '',
    },
  ].filter(Boolean) as { title?: string; breadcrumb?: string; url?: string }[]

  return (
    <FBPage className="gap-y-4" noContainer>
      <ChainHeader
        tableTypeSlug={chainTableTypeSlug}
        category={category}
        subCategory={subCategory}
        name={doorProxyData?.name}
        breadcrumbs={breadcrumbs}
        phone={doorProxyData?.phone_number}
        extension={doorProxyData?.extension}
        address={{
          addressInfo: {
            fullAddress: doorProxyData?.address,
            city: doorProxyData?.city,
            state: doorProxyData?.state,
            zipcode: doorProxyData?.zipcode,
          },
        }}
        domainField={doorProxyData?.domain}
        contactCompanyId={doorProxyData?.chain_proxy}
        doorProxyId={doorProxyData?.id}
        userCreated
        isDoor={true}
        editDomain={() => {
          setEditDoorProxy(doorProxyData!)
        }}
        rightColumnHeader={
          doorProxyData && (
            <CompanyAttributesManager
              doorProxyId={doorProxyData.id}
              isDoorPage={true}
            />
          )
        }
      />

      {!!editDoorProxy && (
        <EditDoorProxyModal
          show={!!editDoorProxy}
          setShow={() => setEditDoorProxy(null)}
          doorProxy={editDoorProxy}
        />
      )}

      {/* FIR-2865: Deals Section */}
      {doorProxyData && (
        <DealsSection
          forCompany={{
            id: doorProxyData.chain_proxy,
          }}
          forDoorProxyId={doorProxyData.id}
          companyTypeSlug={chainTableTypeSlug}
          tableTypeSlug={doorTableTypeSlug}
          isUserCreated
        />
      )}

      {doorProxyData && enableEmailIntegration && (
        <EmailsTableSection
          forChainProxyId={doorProxyData.chain_proxy}
          forDoorProxyId={doorProxyData.id}
        />
      )}

      {/* Notes Section */}
      {doorProxyData && (
        <NotesSection doorProxyId={doorProxyData.id} pagination={true} />
      )}

      {doorProxyData && (
        <ContactsTableSection
          contactCompany={{
            name: doorProxyData.name,
            id: doorProxyData.chain_proxy!,
            domain: chainProxyData?.domain,
          }}
          forDoorProxyId={doorProxyData?.id}
        />
      )}

      {/* FIR-2864: Contacts Section */}
      {/* {doorProxyData && <ContactsTableSection data={doorProxyData} />} */}

      {/* FIR-2851: Emails Section (if feature flag enabled) */}
      {/* {doorProxyData && enableEmailIntegration && (
        <EmailsTableSection forChainProxyId={doorProxyData.id} />
      )} */}

      {/* Delete Button */}
      <Block className="flex items-center justify-center">
        <FbButton
          className="flex-shrink-0"
          variant="danger"
          onClick={handleDelete}
        >
          Delete Door
        </FbButton>
      </Block>
    </FBPage>
  )
}
